import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './DashboardHeader.module.scss'
import { Search, X, SendHorizonal, Users, Gavel, Briefcase, FileText } from 'lucide-react'
import {
  getDesktopSuggestions,
  getMobileResponses,
  normalizeText,
  type DesktopSuggestion,
  type MobileResponse
} from './dashboardActionsConfig.tsx'
import useNavVisibility from '../../../hooks/useNavVisibility.ts'
import { useDispatch, useSelector } from 'react-redux'
import { getTotalUsers, getTotalPublicProjects, getTotalPublicQuotations, getTotalPrivateQuotations, openSuscriptionModal, getAllWorkOffers } from '../../../store/actions'
import { analyzeWithLLM } from '../../../services/llmService.ts'
import { useConversationHistory } from './hooks/useConversationHistory.ts'
import { loadConversations } from './utils/conversationStorage.ts'
import { useMessageLimits } from './hooks/useMessageLimits.ts'
import { useConversationalMode } from './hooks/useConversationalMode.ts'
import ConversationalMode from './ConversationalMode/ConversationalMode.tsx'
import ChatSearchModal from './ConversationalMode/ChatSearchModal.tsx'
import { Message, ConversationSession } from './utils/conversationStorage.ts'

const formatStatValue = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}m`
  }
  if (value >= 1000) {
    const formatted = value / 1000
    return `${formatted % 1 === 0 ? formatted.toFixed(0) : formatted.toFixed(1)}k`
  }
  return value.toString()
}

const formatStatValueNoAbbrev = (value: number): string => {
  return value.toLocaleString('es-AR')
}

const MOBILE_PRIORITY_IDS = ['cotizar-materiales', 'ofrecer-servicios', 'publicar-oferta-trabajo']

export interface MobileResultsPayload {
  status: 'idle' | 'loading' | 'result' | 'empty'
  label?: string
  summary?: string
  cta?: () => void
}

interface DashboardHeaderProps {
  userName: string
  userId?: string
  userData?: any
  onMobileResultsChange?: (payload: MobileResultsPayload) => void
  onConversationalModeChange?: (isActive: boolean) => void
  onAgentStatusChange?: (isProcessing: boolean) => void
  onConversationalActionsReady?: (actions: {
    onCreateNewSession: () => void
    onOpenSearchModal: () => void
    onCloseConversationalMode: () => void
    hasActiveConversation?: boolean
    totalChatsCount?: number
  }) => void
  onSidebarStateChange?: (isCollapsed: boolean) => void
  sidebarCollapsed?: boolean
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  userName,
  userId,
  userData,
  onMobileResultsChange,
  onConversationalModeChange,
  onAgentStatusChange,
  onConversationalActionsReady,
  onSidebarStateChange,
  sidebarCollapsed = false
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [submittedQuery, setSubmittedQuery] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [matchedSuggestion, setMatchedSuggestion] = useState<DesktopSuggestion | null>(null)
  const [llmResponse, setLlmResponse] = useState<string | null>(null)
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false)
  const [showMobileSuggestions, setShowMobileSuggestions] = useState(false)
  const [showLeftFade, setShowLeftFade] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch<any>()
  const totalUsers = useSelector((state: any) => state.dashboard?.totalUsers ?? 0)
  const totalPublicProjects = useSelector((state: any) => state.dashboard?.totalPublicProjects ?? 0)
  const totalJobOffers = useSelector((state: any) => state.register?.allJobRequests?.total ?? 0)
  const totalPublicQuotations = useSelector((state: any) => state.dashboard?.totalPublicQuotations ?? 0)
  const totalPrivateQuotations = useSelector((state: any) => state.dashboard?.totalPrivateQuotations ?? 0)
  const isUserPremium = useSelector((state: any) => state.general?.isUserPremium ?? false)

  const {
    currentSession,
    allSessions,
    createSession,
    loadSession,
    addMessage,
    removeSession,
    clearCurrentSession
  } = useConversationHistory(userId)

  const {
    isActive: isConversationalModeActive,
    activate: activateConversationalMode,
    deactivate: deactivateConversationalMode
  } = useConversationalMode()

  const {
    canSendMessage,
    remainingMessages,
    hasReachedLimit,
    limit
  } = useMessageLimits(currentSession, isUserPremium)
  const [currentStatIndex, setCurrentStatIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const { ocultarEnMobile } = useNavVisibility()
  const isMobile = ocultarEnMobile
  const mobileSuggestionsRef = useRef<HTMLDivElement | null>(null)
  const desktopSuggestions = useMemo(() => getDesktopSuggestions(navigate, userId, userData, dispatch), [navigate, userId, userData, dispatch])
  const mobileResponses = useMemo(() => getMobileResponses(navigate, userId, userData, dispatch), [navigate, userId, userData, dispatch])
  const mobileQuickSuggestions = useMemo(() => {
    const prioritized = MOBILE_PRIORITY_IDS
      .map(id => mobileResponses.find(suggestion => suggestion.id === id))
      .filter((suggestion): suggestion is MobileResponse => Boolean(suggestion))
    const remaining = mobileResponses.filter(suggestion => !MOBILE_PRIORITY_IDS.includes(suggestion.id))
    return [...prioritized, ...remaining].slice(0, 4)
  }, [mobileResponses])
  const totalQuotations = useMemo(() => {
    const publicQuot = typeof totalPublicQuotations === 'number' ? totalPublicQuotations : 0
    const privateQuot = typeof totalPrivateQuotations === 'number' ? totalPrivateQuotations : 0
    return publicQuot + privateQuot
  }, [totalPublicQuotations, totalPrivateQuotations])

  const activityStats = useMemo(
    () => [
      {
        id: 'usuarios-registrados',
        value: formatStatValueNoAbbrev(totalUsers),
        label: 'Usuarios verificados registrados',
        icon: <Users size={16} />
      },
      {
        id: 'licitaciones',
        value: formatStatValue(totalPublicProjects),
        label: 'Licitaciones abiertas hoy',
        icon: <Gavel size={16} />
      },
      {
        id: 'ofertas-laborales',
        value: formatStatValue(totalJobOffers),
        label: 'Ofertas de trabajo vigentes',
        icon: <Briefcase size={16} />
      },
      {
        id: 'cotizaciones',
        value: formatStatValue(totalQuotations),
        label: 'Cotizaciones totales',
        icon: <FileText size={16} />
      }
    ],
    [totalUsers, totalPublicProjects, totalJobOffers, totalQuotations]
  )

  useEffect(() => {
    if (!isMobile) {
      setShowMobileSuggestions(false)
      setShowLeftFade(false)
      return
    }

    const timeout = window.setTimeout(() => {
      setShowMobileSuggestions(true)
    }, 800)

    return () => window.clearTimeout(timeout)
  }, [isMobile])

  useEffect(() => {
    if (!showMobileSuggestions) {
      return
    }

    const element = mobileSuggestionsRef.current
    if (!element) {
      return
    }

    const updateFade = () => {
      setShowLeftFade(element.scrollLeft > 4)
    }

    updateFade()
    element.addEventListener('scroll', updateFade, { passive: true })

    return () => {
      element.removeEventListener('scroll', updateFade)
    }
  }, [showMobileSuggestions])

  useEffect(() => {
    dispatch(getTotalUsers())
    dispatch(getTotalPublicProjects())
    dispatch(getTotalPublicQuotations())
    dispatch(getTotalPrivateQuotations())
    dispatch(getAllWorkOffers())
  }, [dispatch])

  useEffect(() => {
    if (activityStats.length === 0) return

    const interval = setInterval(() => {
      setIsTransitioning(true)
      setTimeout(() => {
        setCurrentStatIndex((prevIndex) => (prevIndex + 1) % activityStats.length)
        setTimeout(() => {
          setIsTransitioning(false)
        }, 50)
      }, 300)
    }, 5000)

    return () => clearInterval(interval)
  }, [activityStats.length])

  const visibleStats = useMemo(() => {
    if (activityStats.length === 0) return []
    const stats = []
    for (let i = 0; i < 3; i++) {
      const index = (currentStatIndex + i) % activityStats.length
      stats.push({ ...activityStats[index], originalIndex: index })
    }
    return stats
  }, [activityStats, currentStatIndex])

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour >= 6 && hour < 12) return "Buenos días"
    if (hour >= 12 && hour < 20) return "Buenas tardes"
    return "Buenas noches"
  }

  const normalizedQuery = normalizeText(searchQuery).trim()
  const hasTypedQuery = normalizedQuery.length > 0
  const shouldShowActivityPulse = true

  const handleClearSearch = () => {
    setSearchQuery('')
    setSubmittedQuery('')
    setMatchedSuggestion(null)
    setLlmResponse(null)
    setIsProcessing(false)
    onAgentStatusChange?.(false)
    setIsFocused(false)
    if (isMobile) {
      onMobileResultsChange?.({ status: 'idle' })
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!hasTypedQuery || isProcessing) {
      return
    }

    if (!isConversationalModeActive) {
      activateConversationalMode()
    }

    const userQuery = searchQuery.trim()
    setIsProcessing(true)
    onAgentStatusChange?.(true)
    if (isMobile) {
      if (showMobileSuggestions) {
        setShowMobileSuggestions(false)
      }
      onMobileResultsChange?.({ status: 'loading' })
    }
    setMatchedSuggestion(null)
    setLlmResponse(null)
    setSubmittedQuery(userQuery)

    // Si no está en modo conversacional, SIEMPRE crear nueva sesión
    // Esto asegura que cada búsqueda inicial inicie una conversación nueva
    let session: ConversationSession
    if (!isConversationalModeActive) {
      session = createSession()
    } else {
      session = currentSession || createSession()
    }

    if (!canSendMessage) {
      setIsProcessing(false)
    onAgentStatusChange?.(false)
      setSearchQuery('')
      return
    }

    const userMessage: Message = {
      id: `msg-${Date.now()}-user`,
      role: 'user',
      content: userQuery,
      timestamp: new Date().toISOString()
    }

    addMessage(userMessage, session.sessionId)

    await new Promise(resolve => setTimeout(resolve, 100))

    const suggestionPool: (DesktopSuggestion | MobileResponse)[] = isMobile ? mobileResponses : desktopSuggestions
    let suggestionMatch: DesktopSuggestion | MobileResponse | undefined = undefined
    let assistantResponse: string | null = null
    let llmResultConfidence = 0
    let llmResultActionId: string | null = null

    try {
      const conversations = loadConversations(userId)
      const currentSessionForLLM = conversations.sessions.find(
        s => s.sessionId === (currentSession?.sessionId || session.sessionId)
      ) || session

      // Solo mensajes anteriores (el backend agrega el mensaje actual del usuario)
      const previousMessages = (currentSessionForLLM.messages || []).slice(-14)
      const historyForLLM = previousMessages.map(m => ({
        role: m.role,
        // Para assistant: enviar el JSON completo para que el LLM vea actionId/guideMode
        // Para user: enviar el contenido normal
        content: m.role === 'assistant' && m.llmRawResponse 
          ? m.llmRawResponse 
          : m.content
      }))

      const llmResult = await analyzeWithLLM(userQuery, historyForLLM)
      assistantResponse = llmResult.response
      setLlmResponse(assistantResponse)
      llmResultConfidence = llmResult.confidence || 0
      llmResultActionId = llmResult.actionId || null

      if (llmResult.actionId) {
        const llmMatch = suggestionPool.find(config => config.id === llmResult.actionId)
        if (llmMatch && llmResult.confidence > 0.5) {
          suggestionMatch = llmMatch
        }
      }

      // Guardar el JSON completo como llmRawResponse para mantener contexto (incluye datos si existen)
      const llmRawResponseObjForSubmit: any = {
        actionId: llmResult.actionId,
        confidence: llmResult.confidence,
        response: assistantResponse,
        guideMode: false
      }

      if (llmResult.needsData && llmResult.data) {
        llmRawResponseObjForSubmit.needsData = true
        llmRawResponseObjForSubmit.data = llmResult.data
        llmRawResponseObjForSubmit.total = llmResult.total || 0
        if ((llmResult as any).quotationType) llmRawResponseObjForSubmit.quotationType = (llmResult as any).quotationType
        if ((llmResult as any).workVisibility) llmRawResponseObjForSubmit.workVisibility = (llmResult as any).workVisibility
        if ((llmResult as any).isPublic !== undefined) llmRawResponseObjForSubmit.isPublic = (llmResult as any).isPublic
        if (llmResult.noResultsMessage) llmRawResponseObjForSubmit.noResultsMessage = llmResult.noResultsMessage
        if ((llmResult as any).searchTerm) llmRawResponseObjForSubmit.searchTerm = (llmResult as any).searchTerm
        if ((llmResult as any).projectType) llmRawResponseObjForSubmit.projectType = (llmResult as any).projectType
      }

      const llmRawResponseForSubmit = JSON.stringify(llmRawResponseObjForSubmit)

      const assistantMessage: Message = {
        id: `msg-${Date.now()}-assistant`,
        role: 'assistant',
        content: assistantResponse,
        timestamp: new Date().toISOString(),
        actionId: llmResult.actionId || null,
        llmRawResponse: llmRawResponseForSubmit,
        ...(llmResult.needsData && llmResult.data && {
          data: llmResult.data,
          dataTotal: llmResult.total || 0,
          needsData: true,
          quotationType: (llmResult as any).quotationType,
          workVisibility: (llmResult as any).workVisibility,
          isPublic: (llmResult as any).isPublic,
          noResultsMessage: llmResult.noResultsMessage
        })
      }

      addMessage(assistantMessage, session.sessionId)
    } catch (error) {
      console.warn('Error al consultar LLM:', error)
      const errorMessage = 'Disculpá, hubo un problema procesando tu consulta. ¿Podrías intentar de nuevo?'
      setLlmResponse(errorMessage)

      const errorMsg: Message = {
        id: `msg-${Date.now()}-error`,
        role: 'assistant',
        content: errorMessage,
        timestamp: new Date().toISOString()
      }
      addMessage(errorMsg, session.sessionId)
    }

    if (suggestionMatch) {
      if (llmResultActionId === 'postularme-ofertas' && llmResultConfidence > 0.6) {
        setMatchedSuggestion(suggestionMatch as DesktopSuggestion)
      } else if (llmResultConfidence > 0.7) {
        setMatchedSuggestion(suggestionMatch as DesktopSuggestion)
      } else {
        setMatchedSuggestion((suggestionMatch as DesktopSuggestion) ?? null)
      }
    } else {
      setMatchedSuggestion(null)
    }
    setIsProcessing(false)
    onAgentStatusChange?.(false)
    if (isMobile) {
      if (suggestionMatch) {
        onMobileResultsChange?.({
          status: 'result',
          label: suggestionMatch.label,
          summary: assistantResponse || (suggestionMatch as MobileResponse).summary,
          cta: suggestionMatch.action
        })
      } else {
        onMobileResultsChange?.({ status: 'empty' })
      }
    }

    setSearchQuery('')
  }

  const handleSendMessage = async (message: string) => {
    if (!canSendMessage) {
      return
    }

    let session = currentSession
    if (!session) {
      session = createSession()
    }

    setIsProcessing(true)

    const userMessage: Message = {
      id: `msg-${Date.now()}-user`,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    }

    addMessage(userMessage)

    await new Promise(resolve => setTimeout(resolve, 100))

    try {
      const conversations = loadConversations(userId)
      const currentSessionForLLM = conversations.sessions.find(
        s => s.sessionId === (currentSession?.sessionId || session.sessionId)
      ) || session

      // Obtener mensajes EXCLUYENDO el userMessage que acabamos de agregar
      // (el backend lo agrega automáticamente como parte de la consulta)
      const allMessages = currentSessionForLLM.messages || []
      
      // Si el último mensaje es el que acabamos de agregar, excluirlo
      const messagesWithoutCurrent = allMessages.length > 0 && 
        allMessages[allMessages.length - 1].id === userMessage.id
        ? allMessages.slice(0, -1)
        : allMessages
      
      const previousMessages = messagesWithoutCurrent.slice(-14)
      
      const historyForLLM = previousMessages.map(m => ({
        role: m.role,
        // Para assistant: enviar el JSON completo para que el LLM vea actionId/guideMode
        // Para user: enviar el contenido normal
        content: m.role === 'assistant' && m.llmRawResponse 
          ? m.llmRawResponse 
          : m.content
      }))

      const llmResult = await analyzeWithLLM(message, historyForLLM)

      // Guardar el JSON completo como llmRawResponse para mantener contexto (incluye datos si existen)
      const llmRawResponseObj: any = {
        actionId: llmResult.actionId,
        confidence: llmResult.confidence,
        response: llmResult.response,
        guideMode: llmResult.guideMode || false
      }

      if (llmResult.needsData && llmResult.data) {
        llmRawResponseObj.needsData = true
        llmRawResponseObj.data = llmResult.data
        llmRawResponseObj.total = llmResult.total || 0
        if ((llmResult as any).quotationType) llmRawResponseObj.quotationType = (llmResult as any).quotationType
        if ((llmResult as any).workVisibility) llmRawResponseObj.workVisibility = (llmResult as any).workVisibility
        if ((llmResult as any).isPublic !== undefined) llmRawResponseObj.isPublic = (llmResult as any).isPublic
        if (llmResult.noResultsMessage) llmRawResponseObj.noResultsMessage = llmResult.noResultsMessage
        if ((llmResult as any).searchTerm) llmRawResponseObj.searchTerm = (llmResult as any).searchTerm
        if ((llmResult as any).projectType) llmRawResponseObj.projectType = (llmResult as any).projectType
      }

      const llmRawResponse = JSON.stringify(llmRawResponseObj)

      const assistantMessage: Message = {
        id: `msg-${Date.now()}-assistant`,
        role: 'assistant',
        content: llmResult.response,
        timestamp: new Date().toISOString(),
        actionId: llmResult.actionId || null,
        guideMode: llmResult.guideMode || false,
        llmRawResponse,
        ...(llmResult.needsData && llmResult.data && {
          data: llmResult.data,
          dataTotal: llmResult.total || 0,
          needsData: true,
          quotationType: (llmResult as any).quotationType,
          workVisibility: (llmResult as any).workVisibility,
          isPublic: (llmResult as any).isPublic,
          noResultsMessage: llmResult.noResultsMessage
        })
      }

      addMessage(assistantMessage)
      onAgentStatusChange?.(false)
    } catch (error) {
      console.warn('Error al consultar LLM:', error)
      const errorMsg: Message = {
        id: `msg-${Date.now()}-error`,
        role: 'assistant',
        content: 'Disculpá, hubo un problema procesando tu consulta. ¿Podrías intentar de nuevo?',
        timestamp: new Date().toISOString()
      }
      addMessage(errorMsg)
    }

    setIsProcessing(false)
    onAgentStatusChange?.(false)
  }

  useEffect(() => {
    onConversationalModeChange?.(isConversationalModeActive)
  }, [isConversationalModeActive, onConversationalModeChange])


  const handleCloseConversationalMode = useCallback(() => {
    deactivateConversationalMode()
    clearCurrentSession()
  }, [deactivateConversationalMode, clearCurrentSession])

  const handleCreateNewSession = useCallback(() => {
    createSession()
    activateConversationalMode()
  }, [createSession, activateConversationalMode])

  const openSearchModalRef = useRef<(() => void) | null>(null)
  const onConversationalActionsReadyRef = useRef(onConversationalActionsReady)

  const handleOpenHistoryModal = useCallback(() => {
    if (isConversationalModeActive && openSearchModalRef.current) {
      openSearchModalRef.current()
    } else {
      setIsHistoryModalOpen(true)
    }
  }, [isConversationalModeActive])

  useEffect(() => {
    onConversationalActionsReadyRef.current = onConversationalActionsReady
  }, [onConversationalActionsReady])

  useEffect(() => {
    if (onConversationalActionsReadyRef.current) {
      const hasActiveConversation = currentSession && currentSession.messages.length > 0
      const actions = {
        onCreateNewSession: handleCreateNewSession,
        onOpenSearchModal: handleOpenHistoryModal,
        onCloseConversationalMode: handleCloseConversationalMode,
        hasActiveConversation: hasActiveConversation,
        totalChatsCount: allSessions.length
      }
      onConversationalActionsReadyRef.current(actions)
    }
  }, [isConversationalModeActive, handleCreateNewSession, handleCloseConversationalMode, handleOpenHistoryModal, currentSession, allSessions.length])

  const handleSuggestionAction = () => {
    if (!matchedSuggestion) {
      return
    }

    matchedSuggestion.action()
    setSearchQuery('')
    setSubmittedQuery('')
    setMatchedSuggestion(null)
    setIsFocused(false)
    if (isMobile) {
      onMobileResultsChange?.({ status: 'idle' })
    }
  }

  if (isConversationalModeActive) {
    return (
      <div className={`${styles.conversationalModeContainer} ${isMobile ? styles.conversationalModeContainerMobile : ''}`}>
        <ConversationalMode
          isActive={isConversationalModeActive}
          messages={currentSession?.messages || []}
          onSendMessage={handleSendMessage}
          onClose={handleCloseConversationalMode}
          isLoading={isProcessing}
          canSendMessage={canSendMessage}
          remainingMessages={remainingMessages}
          hasReachedLimit={hasReachedLimit}
          limit={limit}
          matchedSuggestion={matchedSuggestion}
          onSuggestionAction={handleSuggestionAction}
          allSessions={allSessions}
          currentSessionId={currentSession?.sessionId || null}
          onSelectSession={(sessionId) => {
            const session = loadSession(sessionId)
            if (session) {
              activateConversationalMode()
            }
          }}
          onCreateNewSession={() => {
            createSession()
            activateConversationalMode()
          }}
          onDeleteSession={removeSession}
          userId={userId}
          userData={userData}
          searchModalRef={openSearchModalRef}
          onSidebarStateChange={onSidebarStateChange}
        />
      </div>
    )
  }

  return (
    <div className={styles.initialModeContainer}>
      <div className={styles.header}>
        <div className={styles.topRow}>
          <div className={styles.greetingSection}>
            <h1 className={styles.greeting}>
              {getGreeting()}, <span className={styles.userName}>{userName}</span>
            </h1>
            <p className={styles.subtitle}>¿Qué necesitas hacer hoy?</p>
            {shouldShowActivityPulse && visibleStats.length > 0 && (
              <div className={`${styles.activityPulse} ${isTransitioning ? styles.activityPulseFadeOut : styles.activityPulseFadeIn}`}>
                {visibleStats.map((stat, index) => (
                  <React.Fragment key={`${stat.id}-${stat.originalIndex}`}>
                    <div className={styles.activityItem}>
                      <span className={styles.activityIcon}>{stat.icon}</span>
                      <span className={styles.activityValue}>{stat.value}</span>
                      <span className={styles.activityLabel}>{stat.label}</span>
                    </div>
                    {index < visibleStats.length - 1 && <span className={styles.activityDivider}>•</span>}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>

        {isMobile && (
          <div
            className={`${styles.mobileSuggestions} ${showMobileSuggestions ? styles.mobileSuggestionsVisible : ''}`}
          >
            <div
              className={`${styles.mobileSuggestionsViewport} ${showLeftFade ? styles.mobileSuggestionsViewportLeftFade : ''}`}
            >
              <div className={styles.mobileSuggestionList} ref={mobileSuggestionsRef}>
                {mobileQuickSuggestions.map((suggestion) => (
                  <button
                    key={suggestion.id}
                    type="button"
                    className={styles.mobileSuggestionCard}
                    onClick={() => suggestion.action()}
                  >
                    {suggestion.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={styles.searchSection}>
        <form className={styles.searchForm} onSubmit={handleSubmit}>
          <div className={`${styles.searchBox} ${isFocused ? styles.searchBoxFocused : ''}`}>
            <Search size={20} className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder={isMobile ? '' : 'Contanos qué querés hacer y te ayudamos con la IA de Edify...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setTimeout(() => setIsFocused(false), 200)}
              disabled={isProcessing}
            />
            {searchQuery && !isProcessing && (
              <button
                className={styles.clearButton}
                onClick={handleClearSearch}
                type="button"
              >
                <X size={16} />
              </button>
            )}
            <button
              type="submit"
              className={styles.sendIconButton}
              disabled={!hasTypedQuery || isProcessing}
            >
              <SendHorizonal size={18} />
            </button>
          </div>
        </form>
      </div>

      <ChatSearchModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        sessions={allSessions}
        currentSessionId={currentSession?.sessionId || null}
        onSelectSession={(sessionId) => {
          setIsHistoryModalOpen(false)
          const session = loadSession(sessionId)
          if (session) {
            activateConversationalMode()
          }
        }}
        onCreateNewSession={() => {
          setIsHistoryModalOpen(false)
          createSession()
          activateConversationalMode()
        }}
        onDeleteSession={removeSession}
        userId={userId}
        userData={userData}
      />
    </div>
  )
}

export default DashboardHeader

